/**
 * BYOD dashboard canvas — a CONTROLLED, reusable renderer for a dashboard's
 * rows + tiles, with optional drag-and-drop editing. It owns NO dashboard
 * state: the parent passes `rowOrder`/`rows`/`widgets`, `editable`, the per-tile
 * `filters`, and granular change handlers; the canvas distributes them via
 * `CanvasContext` and calls the handlers on edits. This lets the builder
 * (parent = Zustand store) and any embed (parent = its own state) share one
 * render path, and lets multiple `<Canvas>` instances coexist on a page.
 *
 * DnD architecture (each file reasoned about in isolation):
 *   - `dnd-config.ts`, `sortable-tile.tsx`, `row-end-drop-zone.tsx`,
 *     `droppable-tile-row.tsx`, `inter-row-divider-stack.tsx`,
 *     `hooks/use-drag-indicator.ts`, `hooks/use-byod-keyboard-nav.ts`,
 *     `hooks/use-accessibility-plugin.ts`, `utils/build-drag-end-result.ts`,
 *     `utils/handle-drag-end.ts`.
 */
import * as React from 'react';

import {EmptyState} from '@spaced-out/ui-design-system/lib/components/EmptyState';
import {Icon} from '@spaced-out/ui-design-system/lib/components/Icon';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {DraggableWrapper} from '@spaced-out/ui-design-system/lib/components/DraggableWrapper';
import {
  Toast,
  toastApi,
  ToastTitle,
} from '@spaced-out/ui-design-system/lib/components/Toast';
import {DragDropProvider, DragOverlay} from '@dnd-kit/react';
import type {DragStartEvent, DragEndEvent} from '@dnd-kit/react';
import {Accessibility} from '@dnd-kit/dom';

import {classify} from 'src/utils/classify';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {
  MAX_TILES_PER_ROW,
  MAX_WIDGETS,
} from 'src/components/analytics-genesis/byod/constants';
import {ROW_TYPE, WIDGET_TYPE} from 'src/types/byod';
import type {Row} from 'src/types/byod';

import {ByodDragOverlayProvider} from 'src/components/analytics-genesis/byod/api';
import {CanvasTile} from './canvas-tile';
import {SortableTile} from './sortable-tile';
import {SectionRow} from './section-row';
import {TileDivider} from './tile-divider';
import {TilePlaceholder} from './tile-placeholder';
import {RowEndDropZone} from './row-end-drop-zone';
import {DroppableTileRow} from './droppable-tile-row';
import {InterRowDividerStack} from './inter-row-divider-stack';
import {
  BYOD_SENSORS,
  DRAG_TYPE,
  dropTypeForRow,
  type DragType,
} from './dnd-config';
import {
  CanvasProvider,
  useCanvasContext,
  type CanvasContextValue,
} from './canvas-context';
import {useDragIndicator} from '../hooks/use-drag-indicator';
import {useByodKeyboardNav} from '../hooks/use-byod-keyboard-nav';
import {useAccessibilityPlugin} from '../hooks/use-accessibility-plugin';
import {useExitAnimation} from '../hooks/use-exit-animation';
import type {InvalidHoverReason} from './tile-row';
import {NEW_ROW_PREFIX, decideDragOutcome} from '../utils/handle-drag-end';
import {buildDragEndResult} from '../utils/build-drag-end-result';
import layoutCss from './dashboard-builder-canvas-layout.css';


export type {InvalidHoverReason};

/**
 * Public props — the controlled value (parent owns all of it). The context-backed
 * fields drive tile rendering; `focusTileId`/`onFocusConsumed` are scroll-to-tile
 * signals consumed only by CanvasInner (kept out of the context — see below).
 */
export type CanvasProps = Omit<CanvasContextValue, 'isAtWidgetLimit'> & {
  /** Scroll this tile into view (e.g. after returning from the edit panel). */
  focusTileId?: string | null;
  /** Called once the focus scroll has been consumed. */
  onFocusConsumed?: () => void;
};

// ---------------------------------------------------------------------------
// ByodKeyboardNavBridge — `useByodKeyboardNav` reads the DnD manager from the
// `DragDropProvider` context, so it must run as a child of the provider.
// ---------------------------------------------------------------------------

function ByodKeyboardNavBridge() {
  useByodKeyboardNav();
  return null;
}

function rowClass(type: string): string {
  if (type === ROW_TYPE.KPI) {
    return layoutCss.canvasRowKpi;
  }
  if (type === ROW_TYPE.VIZ) {
    return layoutCss.canvasRowViz;
  }
  return layoutCss.canvasRow;
}

type ExitingRow = ReturnType<typeof useExitAnimation>[number];

function ExitingRowPlaceholder({row}: {row: ExitingRow}) {
  return (
    <div
      className={classify(
        layoutCss.canvasRowExiting,
        row.rowType === ROW_TYPE.KPI && layoutCss.canvasRowExitingKpi,
        row.rowType === ROW_TYPE.VIZ && layoutCss.canvasRowExitingViz,
        row.rowType === ROW_TYPE.SECTION && layoutCss.canvasRowExitingSection,
      )}
      aria-hidden
    />
  );
}

// ---------------------------------------------------------------------------
// Canvas — public controlled wrapper. Derives `isAtWidgetLimit`, provides the
// CanvasContext, and renders the orchestrator.
// ---------------------------------------------------------------------------

export const Canvas = React.memo((props: CanvasProps) => {
  const {focusTileId, onFocusConsumed, ...rest} = props;
  const widgetCount = React.useMemo(
    () =>
      Object.values(rest.widgets).filter(
        (w) => w?.widget_type !== WIDGET_TYPE.SECTION,
      ).length,
    [rest.widgets],
  );
  const isAtWidgetLimit = widgetCount >= MAX_WIDGETS;
  // Build the context value from the individual controlled fields (not the whole
  // props object) and EXCLUDE the focus signals, so the value's identity only
  // changes when a tile-relevant input actually changes. A `focusTileId` change
  // (frequent, on every edit-navigation) therefore no longer re-creates the
  // value and re-renders every tile — the focus signals go straight to
  // CanvasInner as props instead.
  const value = React.useMemo<CanvasContextValue>(
    () => ({
      rowOrder: rest.rowOrder,
      rows: rest.rows,
      widgets: rest.widgets,
      editable: rest.editable,
      dashboardId: rest.dashboardId,
      filters: rest.filters,
      isAtWidgetLimit,
      onMoveTile: rest.onMoveTile,
      onMoveTileToNewRow: rest.onMoveTileToNewRow,
      onRemoveWidget: rest.onRemoveWidget,
      onAddSection: rest.onAddSection,
      onRenameSection: rest.onRenameSection,
      onRemoveSection: rest.onRemoveSection,
      onUpdateWidget: rest.onUpdateWidget,
      onEditWidget: rest.onEditWidget,
      onAddWidget: rest.onAddWidget,
      onExport: rest.onExport,
    }),
    [
      rest.rowOrder,
      rest.rows,
      rest.widgets,
      rest.editable,
      rest.dashboardId,
      rest.filters,
      isAtWidgetLimit,
      rest.onMoveTile,
      rest.onMoveTileToNewRow,
      rest.onRemoveWidget,
      rest.onAddSection,
      rest.onRenameSection,
      rest.onRemoveSection,
      rest.onUpdateWidget,
      rest.onEditWidget,
      rest.onAddWidget,
      rest.onExport,
    ],
  );
  return (
    <CanvasProvider value={value}>
      <CanvasInner
        focusTileId={focusTileId}
        onFocusConsumed={onFocusConsumed}
      />
    </CanvasProvider>
  );
});

// ---------------------------------------------------------------------------
// CanvasInner — DnD orchestration + render loop, reading the controlled value
// from context (no singleton store).
// ---------------------------------------------------------------------------

function CanvasInner({
  focusTileId,
  onFocusConsumed,
}: {
  focusTileId?: string | null;
  onFocusConsumed?: () => void;
}) {
  const ctx = useCanvasContext();
  const {t} = useLabelTranslation();
  const {
    rowOrder,
    rows,
    widgets,
    editable,
    isAtWidgetLimit,
    onMoveTile,
    onMoveTileToNewRow,
    onAddSection,
    onRenameSection,
    onRemoveSection,
    onAddWidget,
  } = ctx;

  // Latest rows for the drag callbacks (avoids stale closures without a store).
  const rowsRef = React.useRef(rows);
  rowsRef.current = rows;

  // Labels for screen-reader announcements + the drag-overlay guard.
  const widgetLabels = React.useMemo(() => {
    const labels: Record<string, string> = {};
    for (const [id, w] of Object.entries(widgets)) {
      labels[id] = w?.label ?? id;
    }
    return labels;
  }, [widgets]);
  const widgetLabelsRef = React.useRef(widgetLabels);
  widgetLabelsRef.current = widgetLabels;

  const [editingRowId, setEditingRowId] = React.useState<string | null>(null);
  const [dragState, setDragState] = React.useState<{
    type: DragType | null;
    id: string | null;
    rowType: string | null;
    width: number | null;
  }>({type: null, id: null, rowType: null, width: null});
  const activeDragType = dragState.type;
  const activeDragId = dragState.id;
  const activeDragRowType = dragState.rowType;
  const activeDragWidth = dragState.width;
  const [hoveredInvalid, setHoveredInvalid] = React.useState<{
    rowId: string;
    reason: InvalidHoverReason;
  } | null>(null);
  const hoveredInvalidRef = React.useRef(hoveredInvalid);
  hoveredInvalidRef.current = hoveredInvalid;

  const handleInvalidHover = React.useCallback(
    (rowId: string | null, reason: InvalidHoverReason | null) => {
      setHoveredInvalid(rowId && reason ? {rowId, reason} : null);
    },
    [],
  );

  const showRejectionToast = React.useCallback(
    (reason: InvalidHoverReason) => {
      let title: string;
      if (reason.kind === 'type-mismatch') {
        title =
          reason.rowType === ROW_TYPE.KPI
            ? t(
                'Only_KPI_tiles_can_be_placed_in_KPI_rows',
                'Only KPI tiles can be placed in KPI rows.',
              )
            : t(
                'Only_charts_and_tables_can_be_placed_in_this_row',
                'Only charts and tables can be placed in this row.',
              );
      } else {
        title = t('This_row_is_full_Maximum_count_tiles_per_row', {
          count: reason.max,
          defaultValue: `This row is full. Maximum ${reason.max} tiles per row.`,
        });
      }
      toastApi.show(
        <Toast initialFocus={0} semantic="information">
          <ToastTitle>{title}</ToastTitle>
        </Toast>,
      );
    },
    [t],
  );

  // Programmatic scroll-to-tile (after "edit" navigation returns).
  React.useEffect(() => {
    if (!focusTileId) {
      return undefined;
    }
    const raf = requestAnimationFrame(() => {
      onFocusConsumed?.();
      try {
        const escaped = CSS.escape(focusTileId);
        const el = document.querySelector<HTMLElement>(
          `[data-tile-id="${escaped}"]`,
        );
        if (el) {
          el.scrollIntoView({behavior: 'smooth', block: 'center'});
        }
      } catch {
        // Selector failures are non-fatal — just skip the scroll.
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [focusTileId, onFocusConsumed]);

  const exitingRows = useExitAnimation(rowOrder, rows);

  const exitingByInsertAt = React.useMemo(() => {
    const map = new Map<number, ExitingRow[]>();
    for (const e of exitingRows) {
      const list = map.get(e.insertAt);
      if (list) {
        list.push(e);
      } else {
        map.set(e.insertAt, [e]);
      }
    }
    return map;
  }, [exitingRows]);

  const handleAddSection = React.useCallback(
    (afterIndex: number) => {
      const newRowId = onAddSection?.(afterIndex);
      if (newRowId) {
        setEditingRowId(newRowId);
      }
    },
    [onAddSection],
  );

  const describeDroppable = React.useCallback(
    (droppableId: string): string => {
      if (droppableId.startsWith(NEW_ROW_PREFIX)) {
        const variant = droppableId.split('-')[2];
        return variant === 'kpi'
          ? t('new_KPI_row', 'new KPI row')
          : t('new_chart_or_table_row', 'new chart or table row');
      }
      const idx = rowOrder.indexOf(droppableId);
      const row = rows[droppableId];
      if (!row || idx === -1) {
        return t('row', 'row');
      }
      const position = idx + 1;
      if (row.type === ROW_TYPE.KPI) {
        return t('KPI_row_position', {
          position,
          defaultValue: `KPI row ${position}`,
        });
      }
      if (row.type === ROW_TYPE.VIZ) {
        return t('chart_and_table_row_position', {
          position,
          defaultValue: `chart and table row ${position}`,
        });
      }
      return row.label || t('section_row', 'section row');
    },
    [rowOrder, rows, t],
  );

  const accessibilityPlugin = useAccessibilityPlugin(describeDroppable, t);

  const onDragStart = React.useCallback((event: DragStartEvent) => {
    const src = event.operation.source;
    if (!src) {
      return;
    }
    const rawData = src.data;
    const srcData =
      rawData != null && typeof rawData === 'object'
        ? (rawData as {type?: string; rowId?: string})
        : undefined;
    const dtype: DragType | null =
      srcData?.type === DRAG_TYPE.KPI || srcData?.type === DRAG_TYPE.VIZ
        ? srcData.type
        : null;

    let rowType: string | null = null;
    if (srcData?.rowId) {
      const row = rowsRef.current[srcData.rowId];
      rowType = row?.type ?? null;
    }

    const srcEl = src.element instanceof HTMLElement ? src.element : null;
    const width = srcEl ? srcEl.getBoundingClientRect().width : null;

    setDragState({type: dtype, id: String(src.id), rowType, width});
    setHoveredInvalid(null);
  }, []);

  const onDragEnd = React.useCallback(
    (event: DragEndEvent) => {
      const invalidAtDrop = hoveredInvalidRef.current;
      setDragState({type: null, id: null, rowType: null, width: null});
      setHoveredInvalid(null);

      const result = buildDragEndResult(event, rowsRef.current);
      if (!result) {
        return;
      }
      const outcome = decideDragOutcome(result, invalidAtDrop);

      switch (outcome.kind) {
        case 'rejected':
          showRejectionToast(outcome.reason);
          return;
        case 'cancelled':
        case 'returned':
        case 'same-position':
        case 'no-op':
          return;
        case 'move-to-new-row':
          onMoveTileToNewRow?.(
            outcome.fromRowId,
            outcome.fromIndex,
            outcome.afterIndex,
            outcome.newRowType,
          );
          return;
        case 'move-tile':
          onMoveTile?.(
            outcome.fromRowId,
            outcome.toRowId,
            outcome.fromIndex,
            outcome.toIndex,
          );
          return;
      }
    },
    [onMoveTile, onMoveTileToNewRow, showRejectionToast],
  );

  const {
    indicatorTarget,
    source: dragSource,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
  } = useDragIndicator({onDragStart, onDragOver: undefined, onDragEnd});

  const getWrapperClass = React.useCallback(
    (rowType: string) => {
      const tileTypeClass =
        rowType === ROW_TYPE.KPI
          ? layoutCss.tileDraggableFillKpi
          : layoutCss.tileDraggableFillViz;
      return editable
        ? classify(
            layoutCss.tileDraggableFill,
            tileTypeClass,
            layoutCss.tileDraggableFillEdit,
          )
        : classify(
            layoutCss.tileDraggableFill,
            tileTypeClass,
            layoutCss.tileDraggableFillView,
          );
    },
    [editable],
  );

  // `any` is intentional: dnd-kit's plugin-list types (Plugins /
  // PluginConstructor / PluginDescriptor) live in @dnd-kit/abstract and are not
  // re-exported from @dnd-kit/react (the package.json dependency). A plugin
  // entry is either a constructor (function) or a {plugin} descriptor; we strip
  // dnd-kit's default Accessibility plugin and append our own.
  const plugins = React.useCallback(
    (defaults: any[]) => [
      ...defaults.filter(
        (p: any) => (typeof p === 'function' ? p : p.plugin) !== Accessibility,
      ),
      accessibilityPlugin,
    ],
    [accessibilityPlugin],
  );

  if (rowOrder.length === 0) {
    return (
      <div className={layoutCss.emptyCanvas} data-testid="byod-canvas-empty">
        <EmptyState
          title={t('No_widgets_yet', 'No widgets yet')}
          imageVariant="data"
        >
          {editable && onAddWidget && (
            <Button
              type="primary"
              size="medium"
              iconLeftName="plus"
              iconLeftType="regular"
              onClick={() => onAddWidget({})}
              testId="byod-canvas-empty-add-report"
            >
              {t('Add_report', 'Add report')}
            </Button>
          )}
        </EmptyState>
      </div>
    );
  }

  const renderTilesAndExtras = ({
    row,
    rowId,
    maxTiles,
    isRowAtCapacity,
    isSourceInThisRow,
    placeholderIndex,
  }: {
    row: Row;
    rowId: string;
    maxTiles: number;
    isRowAtCapacity: boolean;
    isSourceInThisRow: boolean;
    placeholderIndex: number | null;
  }) => {
    const wrapperClass = getWrapperClass(row.type);
    const dropType = dropTypeForRow(row.type);
    const isDragging = activeDragType !== null;
    return (
      <>
        {row.tiles.map((tileId, colIndex) => {
          const showEndDivider =
            !isDragging &&
            editable &&
            row.type !== ROW_TYPE.KPI &&
            colIndex === row.tiles.length - 1 &&
            row.tiles.length < maxTiles;

          return (
            <React.Fragment key={tileId}>
              {placeholderIndex === colIndex && (
                <TilePlaceholder rowType={row.type} />
              )}
              <SortableTile
                tileId={tileId}
                colIndex={colIndex}
                canEdit={editable}
                dropType={dropType}
                rowId={rowId}
                wrapperClass={wrapperClass}
                label={widgetLabels[tileId] ?? tileId}
                disableAsTarget={isRowAtCapacity}
              />
              {!isDragging &&
                colIndex < row.tiles.length - 1 &&
                (editable ? (
                  <TileDivider
                    rowId={rowId}
                    rowType={row.type}
                    colIndex={colIndex + 1}
                    interactive={row.tiles.length < maxTiles}
                  />
                ) : (
                  <div className={layoutCss.tileGap} />
                ))}
              {showEndDivider && (
                <TileDivider
                  rowId={rowId}
                  rowType={row.type}
                  colIndex={row.tiles.length}
                  interactive
                />
              )}
            </React.Fragment>
          );
        })}
        {placeholderIndex !== null && placeholderIndex >= row.tiles.length && (
          <TilePlaceholder rowType={row.type} />
        )}
        <RowEndDropZone
          rowId={rowId}
          dropType={dropType}
          activeDragType={activeDragType}
          isAtCapacity={isRowAtCapacity}
          isSourceInThisRow={isSourceInThisRow}
        />
      </>
    );
  };

  const renderKpiAddButton = (rowId: string, tilesLength: number) => (
    <>
      <div className={layoutCss.tileGap} />
      <button
        type="button"
        className={layoutCss.kpiPlaceholder}
        aria-label={t('Add_KPI', 'Add KPI')}
        onClick={() => {
          if (isAtWidgetLimit) {
            return;
          }
          onAddWidget?.({rowId, col: tilesLength, rowType: ROW_TYPE.KPI});
        }}
      >
        <Icon name="plus" size="medium" color="clickable" />
      </button>
    </>
  );

  const renderTilesRow = (row: Row, rowId: string) => {
    const maxTiles = MAX_TILES_PER_ROW[row.type] ?? Infinity;
    const dropType = dropTypeForRow(row.type);
    const sourceRowId = dragSource?.droppableId ?? null;
    const isSourceInThisRow = sourceRowId === rowId && activeDragType !== null;
    const isRowAtCapacity =
      activeDragType !== null &&
      activeDragType === dropType &&
      row.tiles.length >= maxTiles &&
      sourceRowId !== rowId;
    const showKpiAdd =
      editable &&
      Boolean(onAddWidget) &&
      row.type === ROW_TYPE.KPI &&
      row.tiles.length < maxTiles &&
      activeDragType === null &&
      !isAtWidgetLimit;

    const isCrossRowHover =
      indicatorTarget !== null &&
      indicatorTarget.droppableId === rowId &&
      indicatorTarget.orientation === 'vertical' &&
      !isSourceInThisRow &&
      !isRowAtCapacity;
    const placeholderIndex = isCrossRowHover
      ? Math.min(indicatorTarget!.index, row.tiles.length)
      : null;

    return (
      <DroppableTileRow
        rowId={rowId}
        row={row}
        dropType={dropType}
        activeDragType={activeDragType}
        dragSourceRowId={sourceRowId}
        isRowAtCapacity={isRowAtCapacity}
        indicatorTarget={indicatorTarget}
        suppressInlineIndicator={placeholderIndex !== null}
        hoveredInvalid={hoveredInvalid}
        onInvalidHover={handleInvalidHover}
        rowClassName={rowClass(row.type)}
        tiles={renderTilesAndExtras({
          row,
          rowId,
          maxTiles,
          isRowAtCapacity,
          isSourceInThisRow,
          placeholderIndex,
        })}
        trailingContent={
          showKpiAdd ? renderKpiAddButton(rowId, row.tiles.length) : null
        }
      />
    );
  };

  const renderInterRow = (afterIndex: number) =>
    editable ? (
      <InterRowDividerStack
        afterIndex={afterIndex}
        activeDragType={activeDragType}
        indicatorTarget={indicatorTarget}
        onAddSection={handleAddSection}
      />
    ) : (
      <div className={layoutCss.rowGap} />
    );

  return (
    <DragDropProvider
      sensors={BYOD_SENSORS}
      plugins={plugins}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <ByodKeyboardNavBridge />
      <div className={layoutCss.layoutRows} data-testid="byod-canvas">
        {rowOrder.map((rowId, rowIndex) => {
          const row = rows[rowId];
          if (!row) {
            return null;
          }
          return (
            <React.Fragment key={rowId}>
              {(exitingByInsertAt.get(rowIndex) ?? []).map((e) => (
                <ExitingRowPlaceholder key={`exiting-${e.rowId}`} row={e} />
              ))}
              {renderInterRow(rowIndex - 1)}
              {row.type === ROW_TYPE.SECTION ? (
                <div className={rowClass(row.type)} data-row-type={row.type}>
                  <SectionRow
                    label={row.label ?? ''}
                    canEdit={editable}
                    isEditing={editingRowId === rowId}
                    onStartEdit={() => setEditingRowId(rowId)}
                    onSaveLabel={(label) => {
                      onRenameSection?.(rowId, label);
                      setEditingRowId(null);
                    }}
                    onDelete={() => onRemoveSection?.(rowId)}
                  />
                </div>
              ) : (
                renderTilesRow(row, rowId)
              )}
            </React.Fragment>
          );
        })}
        {exitingRows
          .filter((e) => e.insertAt >= rowOrder.length)
          .map((e) => (
            <ExitingRowPlaceholder key={`exiting-${e.rowId}`} row={e} />
          ))}
        {renderInterRow(rowOrder.length - 1)}
      </div>
      {/*
        dropAnimation={null} — with OptimisticSortingPlugin disabled the source
        stays at its original slot, so the default slide-back animation reads as
        "drop failed." Disabling = clone disappears, real tile re-renders moved.
      */}
      <DragOverlay dropAnimation={null}>
        {activeDragId && activeDragId in widgetLabelsRef.current ? (
          <ByodDragOverlayProvider value={true}>
            <DraggableWrapper
              enableNativeDrag={false}
              isDragging={false}
              wrapperProps={
                activeDragWidth
                  ? {style: {width: `${activeDragWidth}px`}}
                  : undefined
              }
              classNames={{
                wrapper: classify(
                  getWrapperClass(activeDragRowType ?? ROW_TYPE.VIZ),
                  layoutCss.tileDraggableFillDragging,
                ),
                dragHandle: layoutCss.tileDraggableHandleHidden,
              }}
            >
              <CanvasTile tileId={activeDragId} forceReadOnly />
            </DraggableWrapper>
          </ByodDragOverlayProvider>
        ) : null}
      </DragOverlay>
    </DragDropProvider>
  );
}
